// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A form-integrated sparse asset search component using react-hook-form.
 * This component uses server-side filtering via the sparse assets endpoint
 * to efficiently handle large datasets. It displays at most 300 results
 * and shows a result count footer in the dropdown.
 */
import { SparseAsset } from '@/asset/api/types';
import { AssetFunctionalStatusIndicator } from '@/asset/components/functional-status-indicator';
import {
  SPARSE_ASSET_MAX_RESULTS,
  useSparseAssetSearch,
} from '@/asset/hooks/use-sparse-asset-search';
import { SearchResultCountFooter } from '@/components/search-result-count-footer';
import { AssetAssignmentStatus } from '@/constants/asset-assignment-status';
import { SEARCH_TERM_MAXIMUM_CHARACTERS } from '@/constants/search-term';
import {
  Autocomplete,
  AutocompleteRenderInputParams,
  Paper,
  PaperProps,
  TextField,
  Typography,
  debounce,
} from '@mui/material';
import {
  HTMLAttributes,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Control,
  FieldValues,
  UseControllerProps,
  useController,
} from 'react-hook-form';

/**
 * Type definition for the component's value based on selection mode.
 */
type SparseAssetSearchValue<Multiple extends boolean> = Multiple extends true
  ? SparseAsset[]
  : SparseAsset | null;

interface Props<Values extends FieldValues, Multiple extends boolean = false>
  extends UseControllerProps<Values> {
  /** Whether to allow selecting multiple assets */
  multiple?: Multiple;
  /** Label text displayed above the input */
  label: string;
  /** React Hook Form control object */
  control: Control<Values>;
  /** Optional helper text displayed below the input */
  helperText?: string;
  /** Filter by scrapped status (default: false for non-scrapped) */
  scrapped?: boolean;
  /** Filter by project assignment status (only 'none' is currently supported) */
  withProjectStatus?: AssetAssignmentStatus[];
  /** If true, the input is read-only and the dropdown won't open */
  readOnly?: boolean;
  /** Callback fired when selection changes */
  onChange?: (value: SparseAssetSearchValue<Multiple>) => void;
}

/**
 * Determines if two asset options represent the same asset.
 */
function isOptionEqualToValue(option: SparseAsset, value: SparseAsset) {
  return option.id === value.id;
}

/**
 * Generates a display label for an asset option.
 */
function getOptionLabel(option: SparseAsset) {
  return `${option.serial} (${option.item.sku})`;
}

/**
 * Renders an individual asset option in the dropdown list.
 */
function renderOption(
  props: HTMLAttributes<HTMLLIElement> & { key: string },
  option: SparseAsset
) {
  const { key, ...rest } = props;
  return (
    <li key={key} {...rest} aria-label={option.serial}>
      <Typography sx={{ mr: 1 }}>{option.serial}</Typography>
      <AssetFunctionalStatusIndicator status={option.functionalStatus} />
    </li>
  );
}

/**
 * Groups asset options by their product name and SKU.
 */
function groupBy(option: SparseAsset) {
  return `${option.item.sku} (${option.item.name})`;
}

/**
 * Creates a custom Paper component factory that includes the result count footer.
 */
function createPaperWithFooter(totalCount: number, isLoading: boolean) {
  return function PaperWithFooter(props: PaperProps) {
    const { children, ...other } = props;
    return (
      <Paper {...other}>
        {children}
        <SearchResultCountFooter
          shownCount={Math.min(totalCount, SPARSE_ASSET_MAX_RESULTS)}
          totalCount={totalCount}
          isSearching={isLoading}
        />
      </Paper>
    );
  };
}

/**
 * A form-integrated sparse asset search component with server-side filtering.
 * Uses the sparse assets endpoint to efficiently search large datasets,
 * displaying at most 300 results with a result count footer.
 */
export function FormSparseAssetSearch<
  Values extends FieldValues,
  Multiple extends boolean = false,
>(props: Props<Values, Multiple>) {
  const { field, fieldState } = useController({
    name: props.name,
    control: props.control,
  });

  const [inputValue, setInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Debounced search query update
  const debouncedSetSearchQuery = useMemo(
    () => debounce((query: string) => setSearchQuery(query), 300),
    []
  );

  useEffect(() => {
    return () => {
      debouncedSetSearchQuery.clear();
    };
  }, [debouncedSetSearchQuery]);

  const { data, isLoading } = useSparseAssetSearch({
    query: searchQuery,
    scrapped: props.scrapped ?? false,
    withProjectStatus: props.withProjectStatus,
    enabled: !props.readOnly,
  });

  const assets = useMemo(() => {
    return (
      data?.assets
        .slice()
        .sort((asset, other) => asset.serial.localeCompare(other.serial)) ?? []
    );
  }, [data]);

  const totalCount = data?.totalCount ?? 0;

  // Create the custom Paper component with the current state
  const PaperComponent = useMemo(
    () => createPaperWithFooter(totalCount, isLoading),
    [totalCount, isLoading]
  );

  // Handle input value changes with debounced search
  const handleInputChange = useCallback(
    (_: unknown, value: string) => {
      setInputValue(value);
      debouncedSetSearchQuery(value);
    },
    [debouncedSetSearchQuery]
  );

  // Handle selection changes
  const handleChange = useCallback(
    (_: unknown, value: SparseAssetSearchValue<Multiple>) => {
      field.onChange(value);
      if (props.onChange) {
        props.onChange(value);
      }
    },
    [field, props]
  );

  // Handle undefined values based on selection mode
  const value =
    field.value ??
    ((props.multiple ? [] : null) as SparseAssetSearchValue<Multiple>);
  const helperText = props.helperText ?? fieldState.error?.message;

  const renderInput = (parameters: AutocompleteRenderInputParams) => {
    return (
      <TextField
        inputRef={field.ref}
        name={field.name}
        label={props.label}
        error={Boolean(fieldState.error)}
        helperText={helperText}
        onBlur={(event) => {
          if (event.target) {
            event.target.setSelectionRange(0, 0);
          }
        }}
        {...parameters}
        inputProps={{
          ...parameters.inputProps,
          maxLength: SEARCH_TERM_MAXIMUM_CHARACTERS,
        }}
      />
    );
  };

  return (
    <Autocomplete
      fullWidth
      size="small"
      clearOnBlur={false}
      open={props.readOnly ? false : undefined}
      disabled={props.readOnly || props.disabled}
      forcePopupIcon={!props.readOnly}
      value={
        (value ?? (props.multiple ? [] : null)) as Multiple extends true
          ? SparseAsset[]
          : SparseAsset | null
      }
      options={assets}
      loading={isLoading}
      multiple={props.multiple}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      onChange={handleChange}
      groupBy={groupBy}
      renderInput={renderInput}
      renderOption={renderOption}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      filterOptions={(x) => x} // Disable client-side filtering
      filterSelectedOptions={props.multiple}
      loadingText="Loading..."
      noOptionsText={searchQuery ? 'No assets found' : 'Type to search'}
      PaperComponent={PaperComponent}
      slotProps={{
        popper: {
          sx: { minWidth: 300, width: 'fit-content' },
        },
        paper: {
          sx: {
            width: 'fit-content',
            minWidth: '100%',
            '& .MuiAutocomplete-groupLabel': {
              whiteSpace: 'nowrap',
            },
          },
        },
      }}
    />
  );
}
